import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

const Icons = {
    danger: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#dc2626' }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    warning: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ca8a04' }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    ),
    info: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#2563eb' }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    )
};

const ConfirmDialog = ({
    isOpen,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger',
    onConfirm,
    onCancel,
    loading = false
}) => {
    const overlayRef = useRef(null);

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen && !loading) {
                onCancel();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent body scroll
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onCancel, loading]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (overlayRef.current === e.target && !loading) {
            onCancel();
        }
    };

    const getConfirmBtnClass = () => {
        switch (variant) {
            case 'danger': return 'crm-btn crm-btn-danger';
            case 'warning': return 'crm-btn crm-btn-warning'; // Assuming warning class exists or fallback
            case 'info': return 'crm-btn crm-btn-primary';
            default: return 'crm-btn crm-btn-primary';
        }
    };

    const buttonStyle = variant === 'warning' ? {
        backgroundColor: '#ca8a04',
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '4px',
        fontWeight: '500',
        cursor: 'pointer'
    } : {};

    return ReactDOM.createPortal(
        <div
            ref={overlayRef}
            onClick={handleOverlayClick}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                animation: 'crm-fade-in 0.2s ease-out',
            }}
        >
            <div
                style={{
                    background: 'var(--crm-bg, #ffffff)',
                    padding: '24px',
                    borderRadius: '8px',
                    boxShadow: 'var(--crm-shadow-lg, 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04))',
                    width: '100%',
                    maxWidth: '400px',
                    border: '1px solid var(--crm-gray-200, #e5e7eb)',
                    animation: 'crm-slide-up 0.2s ease-out',
                }}
            >
                <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '20px',
                        backgroundColor: variant === 'danger' ? '#fee2e2' : variant === 'warning' ? '#fef3c7' : '#dbeafe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        {Icons[variant] || Icons.info}
                    </div>
                    <div>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--crm-gray-900, #111827)' }}>
                            {title}
                        </h3>
                        <p style={{ margin: 0, fontSize: '14px', color: 'var(--crm-gray-500, #6b7280)', lineHeight: '1.5' }}>
                            {message}
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                    <button
                        className="crm-btn crm-btn-secondary"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        {cancelText}
                    </button>
                    <button
                        className={getConfirmBtnClass()}
                        style={Object.keys(buttonStyle).length ? buttonStyle : {}}
                        onClick={onConfirm}
                        disabled={loading}
                    >
                        {loading ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="crm-spinner-sm"></div>
                                Processing...
                            </div>
                        ) : confirmText}
                    </button>
                </div>
            </div>
            <style>{`
                @keyframes crm-fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes crm-slide-up {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .crm-spinner-sm {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255,255,255,0.3);
                    border-radius: 50%;
                    border-top-color: #fff;
                    animation: spin 1s ease-in-out infinite;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>,
        document.body
    );
};

export default ConfirmDialog;
